import {
  AppBar,
  Box,
  Button,
  Dialog,
  Paper,
  DialogContent,
  IconButton,
  Stack,
  Toolbar,
  Typography,
  Grid,
} from "@mui/material";
import {
  Close as CloseIcon,
  Edit as EditIcon,
  SsidChart as DownloadECGIcon,
  SignalCellularAlt as DownloadParamsIcon,
} from "@mui/icons-material";
import { useGetPatientReports, useGetReportById } from "../useReportQueries";
import { LoadingError, LoadingPaper } from "../../LoadingComponents";
import { useEffect, useRef, useState } from "react";
import { useGetPatientById } from "../../PatientComponents";
import CanvasJSReact from "@canvasjs/react-charts";
import {
  prettify,
  renderEUDate,
  renderEUDateTime,
  renderDateUTC,
  convertServerToLocalDatetime,
} from "../../../utils/utils";
import { saveAs } from "file-saver";
import { useAuthHeader } from "react-auth-kit";
import { buildECGLayoutsFromRaw } from "./reportUtils/ecgReport";
import {
  resolveECGRaw,
  resolveReportAssetUrl,
} from "./reportUtils/reportAssetUtils";

function ECGReport({ report_id, patient_id, closeModal }) {
  var CanvasJSChart = CanvasJSReact.CanvasJSChart;

  // === web service queries/mutations ===
  const authHeader = useAuthHeader();
  const reportQuery = useGetReportById(report_id);
  const patientQuery = useGetPatientById(patient_id);

  // === report display ===
  const [layouts, setLayouts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [ecgLoadError, setEcgLoadError] = useState(null);

  const saveFile = (url, filename) => {
    const resolved = resolveReportAssetUrl(url);
    if (resolved) saveAs(resolved, filename);
  };

  useEffect(() => {
    if (loaded || !reportQuery.isSuccess) return;

    resolveECGRaw(reportQuery.data, authHeader)
      .then((ecgRaw) => {
        setLayouts(buildECGLayoutsFromRaw(reportQuery.data, ecgRaw));
        setLoaded(true);
      })
      .catch((err) => {
        setEcgLoadError(err);
      });
  }, [reportQuery.isSuccess, reportQuery.data, loaded, authHeader]);

  return (
    <>
      <AppBar sx={{ position: "relative" }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={closeModal}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <DialogContent>
        {reportQuery.isLoading ? (
          <LoadingPaper />
        ) : reportQuery.isError ? (
          <LoadingError error={reportQuery.error} />
        ) : ecgLoadError ? (
          <LoadingError error={ecgLoadError} />
        ) : !loaded ? (
          <LoadingPaper />
        ) : (
          <Stack spacing={2} direction={"column"}>
            <Paper sx={{ padding: "10px" }}>
              <Box sx={{ width: "100%" }}>
                <Box sx={{ float: "left" }}>
                  <Typography fontWeight={"bold"} variant="h5">
                    Grafico ECG
                  </Typography>
                </Box>
                {"Params" in reportQuery.data.data && (
                  <Box sx={{ float: "right" }}>
                    <Stack direction={"row"} gap={2}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          saveFile(
                            reportQuery.data.data.ECG,
                            `${patientQuery.data.first_name}_${patientQuery.data.last_name}_${reportQuery.data.date}_ecg.pdf`
                          );
                        }}
                      >
                        <DownloadECGIcon />
                        Scarica ECG
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          saveFile(
                            reportQuery.data.data.Params,
                            `${patientQuery.data.first_name}_${patientQuery.data.last_name}_${reportQuery.data.date}_parametri.pdf`
                          );
                        }}
                      >
                        <DownloadParamsIcon />
                        Scarica Parametri
                      </Button>
                    </Stack>
                  </Box>
                )}
              </Box>

              <Grid container>
                <Grid item xs={6}>
                  <Typography>
                    Assitito:{" "}
                    {patientQuery.isLoading
                      ? ""
                      : `${patientQuery.data.first_name} ${patientQuery.data.last_name}`}
                  </Typography>
                  <Typography>
                    Dispositivo: {prettify(reportQuery.data.origin)}
                  </Typography>
                  <Typography>
                    Data misurazione:
                    {renderEUDateTime(
                      convertServerToLocalDatetime(reportQuery.data.data.Date)
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  {/* <Typography>
                    Durata: {reportQuery.data.data.Duration}
                  </Typography> */}
                  {reportQuery.data.data.HR !== null && (
                    <Typography>
                      Frequenza Cardiaca: {reportQuery.data.data.HR}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Paper>
            {reportQuery.isLoading ? (
              <LoadingPaper />
            ) : reportQuery.isError ? (
              <LoadingError error={reportQuery.error} />
            ) : (
              <Paper sx={{ padding: "10px" }}>
                {layouts !== undefined &&
                  loaded &&
                  layouts.map((layout, index) => (
                    <CanvasJSChart
                      key={layout?.title?.text ?? `ecg-layout-${index}`}
                      options={layout}
                    />
                  ))}
              </Paper>
            )}
          </Stack>
        )}
      </DialogContent>
    </>
  );
}

export default ECGReport;
